<template>
  <div class="search-results">
    <template v-if="store && store.queryStatus == 'ready' && store.filterOptionsStatus == 'ready'">
      <div class="search-results-header">
        <div class="row align-items-center">
          <div class="col-12 col-xl">
            <div class="search-results-summary">
              <template v-if="store.items.length === 0">
                <template v-if="searchTermMessage">
                  <div class="search-results-found-message">
                    {{ noResultsLabel }} {{ forSearchTermLabel }} "<strong>{{ searchTermMessage }}</strong>".
                  </div>
                </template>
                <template v-else>
                  <div class="search-results-found-message">
                    {{ noResultsLabel }}.
                  </div>
                </template>
              </template>
              <template v-if="store.items.length > 0">
                <div class="search-results-found-message">
                  <template v-if="searchTermMessage">
                    <strong>{{ store.totalNumberOfResults }} {{ resultsWord }}</strong> {{ foundWord }} {{
                      forSearchTermLabel
                    }} "<strong>{{ searchTermMessage }}</strong>":
                  </template>
                  <template v-else>
                    <strong>{{ store.totalNumberOfResults }} {{ resultsWord }}</strong> {{ foundWord }}:
                  </template>
                </div>
                <div class="search-results-pagination-message">
                  {{ showingResultsLabel }} {{ store.query.resultsPerPage * (store.query.pageNumber - 1) + 1 }} - {{
                    Math.min(store.query.resultsPerPage * store.query.pageNumber, store.totalNumberOfResults)
                  }}
                </div>
              </template>
            </div>
          </div>
          <div class="col-12 col-xl-auto">
            <form class="search-results-filters" @submit.prevent="applyFilters">
              <div class="search-results-filter-item">
                <label class="visually-hidden" for="searchFilterDate">
                  {{ dateLabel }}
                </label>
                <select class="form-select" v-model="date" id="searchFilterDate">
                  <option value="0">{{ filterByLabel }} {{ dateLabel }}</option>
                  <option v-for="date in store.filterOptions.dates" :key="date.id" :value="date.id">
                    {{ date.label }}
                  </option>
                </select>
              </div>
              <template v-if="store.filterOptions.categories.length > 0">
                <div class="search-results-filter-item">
                  <label class="visually-hidden" for="searchFilterCategory">
                    {{ categoryLabel }}
                  </label>
                  <select class="form-select" v-model="categoryId" id="searchFilterCategory">
                    <option value="0">{{ filterByLabel }} {{ categoryLabel }}</option>
                    <option v-for="category in store.filterOptions.categories" :key="category.id" :value="category.id">
                      {{ category.label }}
                    </option>
                  </select>
                </div>
              </template>
              <div class="search-results-filter-item">
                <button class="btn btn-white border-dark search-result-filter-button" type="submit"
                  v-if="showApplyFiltersButton">
                  {{ applyFiltersLabel }}
                </button>
                <button class="btn btn-black search-result-filter-button" type="button" @click="resetFilters" v-else>
                  {{ resetFiltersLabel }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div class="search-results-items" id="searchResultsItems">
        <div class="row">
          <template v-if="searchType === 'pages'">
            <div class="col-12 mb-4" v-for="item in store.items">
              <div class="search-result-item">
                <div class="search-result-item-title">
                  {{ item.title }}
                </div>
                <template v-if="item.redirectUrl">
                  <a :href="item.redirectUrl" target="_blank">
                    {{ item.redirectUrl }}
                  </a>
                </template>
                <template v-else>
                  <a :href="item.url">
                    {{ item.url }}
                  </a>
                  <div class="search-result-item-date">
                    {{ item.date }}
                  </div>
                </template>
                <p>
                  {{ item.content }}
                </p>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="col-12 col-sm-6 col-xl-4 mb-4" v-for="item in store.items">
              <a class="newsroom-card grow" :class="`newsroom-card-${searchType}`" :href="item.url">
                <img class="newsroom-card-image" :src="item.image ? item.image : defaultImage" alt="">
                <div class="newsroom-card-details">
                  <div class="newsroom-card-date">
                    {{ item.date }}
                  </div>
                  <div class="newsroom-card-title">
                    {{ item.title }}
                  </div>
                  <div class="newsroom-card-details-bottom">
                    <span class="btn btn-secondary">
                      {{ item.buttonText }}
                    </span>
                  </div>
                </div>
              </a>
            </div>
          </template>
        </div>
      </div>
      <div class="search-results-pagination">
        <Pagination :store-name="'search'" :previous-label="paginationPreviousLabel"
          :next-label="paginationNextLabel" />
      </div>
    </template>
    <template v-else>
      <div class="d-flex justify-content-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { storeToRefs } from 'pinia'
import { nextTick } from 'vue'
import Mark from 'mark.js'
import { SearchStore } from '../stores/SearchStore'
import Pagination from './Pagination.vue'

export default {
  props: {
    defaultImage: {
      type: String,
      required: true
    },
    applyFiltersLabel: {
      type: String,
      required: true
    },
    resetFiltersLabel: {
      type: String,
      required: true
    },
    filterByLabel: {
      type: String,
      required: true
    },
    dateLabel: {
      type: String,
      required: true
    },
    categoryLabel: {
      type: String,
      required: true
    },
    resultsWord: {
      type: String,
      required: true
    },
    foundWord: {
      type: String,
      required: true
    },
    forSearchTermLabel: {
      type: String,
      required: true
    },
    showingResultsLabel: {
      type: String,
      required: true
    },
    noResultsLabel: {
      type: String,
      required: true
    },
    paginationPreviousLabel: {
      type: String,
      required: true
    },
    paginationNextLabel: {
      type: String,
      required: true
    },
    searchType: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      valuesInitialized: false,
      date: 0,
      categoryId: 0,
      searchTerm: ''
    }
  },
  computed: {
    searchTermMessage() {
      let searchTermText = this.store.query.searchTerm
      const dateFilterText = this.store.filterOptions.dates.find(d => d.id == this.store.query.date)?.label.replace(/^/, '+')
      const categoryFilterText = this.store.filterOptions.categories.find(c => c.id == this.store.query.categoryId)?.label.replace(/^/, '+')
      const messages = [searchTermText, dateFilterText, categoryFilterText].filter(f => f)
      return messages.join(' ')
    },
    showApplyFiltersButton() {
      return (this.date === 0 && this.categoryId === 0) || (this.date !== this.store.query.date || this.categoryId !== this.store.query.categoryId)
    }
  },
  watch: {
    async queryStatus() {
      // Highlight the search term occurrences in pages results items
      if (this.store.queryStatus === 'ready' && this.store.type === 'pages' && this.store.query.searchTerm && this.store.items.length > 0) {
        await nextTick()
        const searchResultsItemsElement = document.querySelector('#searchResultsItems')
        if (searchResultsItemsElement) {
          const regExpTerm = new RegExp(this.store.query.searchTerm, 'gi')
          const mark = new Mark(searchResultsItemsElement)
          mark.unmark()
          mark.markRegExp(regExpTerm, { element: 'span', className: 'highlight' })
        }
      }
    }
  },
  setup(props) {
    const store = SearchStore()
    const { queryStatus } = storeToRefs(store)
    store.setSearchType(props.searchType)
    store.updateQueryFromUrlParameters()
    store.getFilterOptions()
    store.getResultsFromQuery()
    return {
      store, queryStatus
    }
  },
  created() {
  },
  mounted() {
    this.store.$subscribe((mutation, state) => {
      if (!this.valuesInitialized && state.urlParametersStatus === 'ready') {
        this.initializeValues()
      }
    })
  },
  methods: {
    initializeValues() {
      this.valuesInitialized = true
      if (this.store.query.date) {
        this.date = this.store.query.date
      }
      if (this.store.query.categoryId) {
        this.categoryId = this.store.query.categoryId
      }
    },
    applyFilters() {
      this.store.updateSearch(this.store.query.searchTerm, this.date, this.categoryId)
    },
    resetFilters() {
      this.date = 0
      this.categoryId = 0
      this.store.updateSearch(this.store.query.searchTerm, 0, 0)
    }
  },
  components: {
    Pagination
  }
}
</script>
